import { useParams, Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/data';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';

const categoryArticlesMap: Record<string, typeof articles> = {};
articles.forEach((a) => {
  if (!categoryArticlesMap[a.category]) {
    categoryArticlesMap[a.category] = [];
  }
  categoryArticlesMap[a.category].push(a);
});

const allArticleImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=A%20dramatic%20dark%20cinematic%20scene%20of%20a%20luxury%20hotel%20corridor%20at%20night%2C%20mysterious%20atmosphere%2C%20silhouette%20of%20two%20people%2C%20warm%20dim%20lighting%2C%20noir%20style%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast%2C%20elegant%20composition&width=600&height=400&seq=cat-article-01&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=A%20dark%20minimalist%20scene%20with%20scattered%20financial%20documents%20on%20a%20glossy%20black%20desk%2C%20dramatic%20lighting%20from%20above%2C%20red%20seal%20stamps%2C%20luxury%20atmosphere%2C%20editorial%20style%2C%20dark%20background%2C%20high%20contrast&width=600&height=400&seq=cat-article-02&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=A%20dark%20TV%20studio%20set%20with%20dramatic%20red%20and%20blue%20stage%20lighting%2C%20empty%20chairs%2C%20tense%20atmosphere%2C%20scattered%20props%2C%20cinematic%20style%2C%20dark%20background%2C%20editorial%20photography&width=600&height=400&seq=cat-article-03&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=An%20empty%20movie%20theater%20with%20red%20seats%20in%20darkness%2C%20single%20projector%20beam%20cutting%20through%2C%20eerie%20atmosphere%2C%20cinematic%20noir%20style%2C%20dark%20background%2C%20editorial%20photography&width=600&height=400&seq=cat-article-04&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=A%20dark%20scene%20of%20a%20streaming%20studio%20with%20multiple%20screens%20showing%20chat%20messages%2C%20dim%20pink%20and%20purple%20ambient%20lighting%2C%20empty%20gaming%20chair%2C%20cyberpunk%20vibe%2C%20dark%20background%2C%20editorial%20photography&width=600&height=400&seq=cat-article-05&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=A%20dark%20dramatic%20scene%20of%20a%20director%20chair%20on%20an%20empty%20film%20set%2C%20spotlight%20casting%20long%20shadows%2C%20mysterious%20atmosphere%2C%20cinematic%20noir%20style%2C%20dark%20background%2C%20editorial%20photography&width=600&height=400&seq=cat-article-06&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=A%20dark%20romantic%20scene%20with%20two%20matching%20luxury%20bracelets%20on%20black%20velvet%2C%20soft%20moody%20lighting%2C%20mysterious%20atmosphere%2C%20editorial%20photography%2C%20dark%20background%2C%20high%20contrast&width=600&height=400&seq=cat-article-07&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=A%20dark%20scene%20of%20a%20talent%20show%20stage%20with%20voting%20screens%20showing%20numbers%2C%20dramatic%20blue%20and%20red%20lighting%2C%20empty%20audience%20seats%2C%20cinematic%20style%2C%20dark%20background&width=600&height=400&seq=cat-article-08&orientation=landscape',
  9: 'https://readdy.ai/api/search-image?query=A%20dark%20scene%20of%20luxury%20brand%20products%20shattered%20on%20black%20marble%20floor%2C%20dramatic%20lighting%2C%20broken%20glass%20reflections%2C%20luxury%20noir%20style%2C%20dark%20background&width=600&height=400&seq=cat-article-09&orientation=landscape',
  10: 'https://readdy.ai/api/search-image?query=A%20dark%20office%20scene%20with%20empty%20desks%20under%20dim%20fluorescent%20lighting%2C%20overtime%20atmosphere%2C%20abandoned%20coffee%20cups%2C%20dystopian%20corporate%20vibe%2C%20dark%20background&width=600&height=400&seq=cat-article-10&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=A%20dark%20scene%20of%20contract%20documents%20with%20red%20seals%20on%20black%20desk%2C%20dramatic%20lighting%2C%20mysterious%20atmosphere%2C%20corporate%20noir%20style%2C%20dark%20background&width=600&height=400&seq=cat-article-11&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=A%20dark%20dramatic%20scene%20of%20a%20wedding%20ring%20broken%20in%20half%20on%20black%20surface%2C%20moody%20lighting%2C%20emotional%20atmosphere%2C%20cinematic%20minimalist%20style%2C%20dark%20background&width=600&height=400&seq=cat-article-12&orientation=landscape',
};

const featuredImagesByCategory: Record<string, string> = {
  'mingxing-heidong': 'https://readdy.ai/api/search-image?query=Dark%20glamorous%20celebrity%20silhouette%20on%20red%20carpet%20with%20paparazzi%20flashes%2C%20mysterious%20atmosphere%2C%20cinematic%20noir%20style%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast%2C%20dramatic%20lighting&width=800&height=600&seq=feat-mingxing&orientation=landscape',
  'zongyi-chigua': 'https://readdy.ai/api/search-image?query=Dark%20TV%20studio%20set%20with%20dramatic%20stage%20lighting%2C%20empty%20chairs%20and%20microphones%2C%20cinematic%20atmosphere%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast%2C%20professional%20broadcast%20style&width=800&height=600&seq=feat-zongyi&orientation=landscape',
  'yingshi-jiemi': 'https://readdy.ai/api/search-image?query=Dark%20cinema%20screen%20with%20film%20projector%20beam%2C%20mysterious%20movie%20set%20atmosphere%2C%20director%20chair%2C%20cinematic%20noir%20style%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast&width=800&height=600&seq=feat-yingshi&orientation=landscape',
  'meiri-dasai': 'https://readdy.ai/api/search-image?query=Dark%20explosive%20breaking%20news%20concept%20with%20glowing%20red%20headlines%2C%20dramatic%20atmosphere%2C%20cinematic%20style%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast%2C%20newsroom%20vibe&width=800&height=600&seq=feat-dasai&orientation=landscape',
  'hulianwang-gua': 'https://readdy.ai/api/search-image?query=Dark%20digital%20social%20media%20concept%20with%20glowing%20screens%2C%20cyber%20atmosphere%2C%20smartphone%20and%20laptop%2C%20dark%20background%2C%20editorial%20photography%2C%20high%20contrast%2C%20modern%20tech%20style&width=800&height=600&seq=feat-hulianwang&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-50 mb-2">分类不存在</h1>
            <p className="text-foreground-500 mb-4">请检查访问的分类地址是否正确</p>
            <Link to="/" className="text-primary-400 hover:text-primary-300 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = categoryArticlesMap[category.slug] || [];
  const recommendedArticles = articles
    .filter((a) => a.category !== category.slug)
    .slice(0, 3);

  return (
    <>
      <WebPageSchema
        pageUrl={`http://921xwm.cn/category/${category.slug}`}
        pageName={`${category.name} - 51爆料网`}
        description={category.description}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'http://921xwm.cn/' },
          { name: category.name, url: `http://921xwm.cn/category/${category.slug}` },
        ]}
      />

      <Header />

      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[480px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={featuredImagesByCategory[category.slug] || featuredImagesByCategory['mingxing-heidong']}
              alt={`${category.name} - 51爆料网`}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50" />
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-20">
            <div className="max-w-7xl mx-auto">
              <nav aria-label="面包屑导航" className="mb-4">
                <ol className="flex items-center gap-2 text-sm text-foreground-400">
                  <li><Link to="/" className="hover:text-primary-400 transition-colors">首页</Link></li>
                  <li><span className="text-foreground-600">/</span></li>
                  <li><span className="text-primary-400">{category.name}</span></li>
                </ol>
              </nav>

              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-4">
                {category.name}
              </h1>
              <p className="text-foreground-400 text-base max-w-2xl">
                {category.description}
              </p>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="px-4 md:px-6 pt-8 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => {
                const isCurrent = cat.slug === category.slug;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`shrink-0 px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-all duration-200 ${
                      isCurrent
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-400 hover:text-foreground-200 hover:bg-background-200 border border-background-300/30'
                    }`}
                  >
                    {cat.name}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Article Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50">
                {category.name}爆料
              </h2>
              <span className="text-foreground-500 text-sm">共 {categoryArticles.length} 篇</span>
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <article key={article.id} className="group relative rounded-lg overflow-hidden bg-background-200/70 hover:bg-background-200 transition-colors duration-300">
                    <Link to={`/article/${article.id}`} className="block">
                      <div className="relative h-52 md:h-56 overflow-hidden">
                        <img
                          src={allArticleImages[article.id]}
                          alt={article.title}
                          title={`${article.title} - 51爆料网`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background-200/90 via-transparent to-transparent" />
                        <span className="absolute top-3 right-3 px-2 py-0.5 bg-accent-500/90 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          热度
                        </span>
                      </div>
                      <div className="p-4">
                        <h3 className="text-foreground-100 font-heading font-semibold text-sm leading-snug line-clamp-2 group-hover:text-primary-400 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-2 line-clamp-2 leading-relaxed">
                          {article.summary}
                        </p>
                        <div className="flex items-center gap-3 mt-3 text-foreground-600 text-xs">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line" />
                            {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-3-line" />
                            {article.comments}
                          </span>
                          <span className="ml-auto text-foreground-700">{article.publishDate}</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-600 mb-3 block" />
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>

        {/* Recommended */}
        {recommendedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 text-center mb-10">
                小编力荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {recommendedArticles.map((article) => (
                  <article key={article.id} className="group relative rounded-lg overflow-hidden h-60 md:h-64">
                    <Link to={`/article/${article.id}`} className="block w-full h-full">
                      <img
                        src={allArticleImages[article.id]}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <span className="text-foreground-600 text-xs mb-1 block">{article.publishDate}</span>
                        <h3 className="text-foreground-50 font-heading font-semibold text-sm leading-snug line-clamp-2">
                          {article.title}
                        </h3>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}